import type { IOrdersEdgesNode } from "../queries/customer-gql";
import { useMemo } from "react";
import type { IOrderInfo } from "../api/fetchOrder";

export default function useOrderInfo(order?: IOrdersEdgesNode | null, orderInfo?: IOrderInfo | null) {
  // 총 상품 갯수
  const totalQuantity = useMemo(() => order?.lineItems.edges.reduce((a, b) => a + b.node.quantity, 0) || 0, [order]);

  // 배송비
  const shippingAmount = useMemo(() => {
    if (order) {
      return parseFloat(order.totalShippingPrice.amount);
    }

    return parseFloat(orderInfo?.totalShippingPrice.amount || "0");
  }, [order, orderInfo]);

  // 주문 원래 금액
  const subTotalAmount = useMemo(() => {
    if (order) {
      return parseFloat(order.subtotalPrice?.amount || "0");
    }

    return orderInfo?.originalTotalPriceAmount || 0;
  }, [order, orderInfo]);

  // 상품 원가의 총 금액 (모든 lineItems의 CompareAtPrice 합한 금액)
  // line item의 총 금액
  const lineItemAmount = useMemo(() => {
    if (!order) return orderInfo?.originalTotalPriceAmount ?? 0;
    // line items의 variant가 다 있는지 (null 이 없는지 확인)
    const hasAllVariants = order.lineItems.edges.every((lineItem) => lineItem.node.variant);

    if (hasAllVariants) {
      return (
        order.lineItems.edges.reduce((sum, lineItem) => {
          const compareAtPrice = lineItem.node.variant?.compareAtPrice
            ? parseFloat(lineItem.node.variant.compareAtPrice.amount)
            : null;
          const price = lineItem.node.variant?.price ? parseFloat(lineItem.node.variant.price.amount) : 0;
          const itemTotal = lineItem.node.quantity * (compareAtPrice ? compareAtPrice : price);
          return sum + itemTotal;
        }, 0) || 0
      );
    }

    //  line items의 variant가 하나라도 null 인 경우 실 결제 금액으로 노출되도록 함
    return parseFloat(order.subtotalPrice?.amount ?? "0");
  }, [order, orderInfo]);

  // 환불된 금액
  const refundAmount = useMemo(() => {
    if (order) {
      return parseFloat(order.totalRefunded.amount);
    }

    return parseFloat(orderInfo?.totalRefunded.amount ?? "0");
  }, [order, orderInfo]);

  // 상시 할인 금액
  const autoDiscountAmount = useMemo(() => {
    if (!order) return 0;

    return (
      order.lineItems.edges
        .flatMap(({ node }) => node.discountAllocations)
        .filter(
          (item) =>
            item.discountApplication.__typename === "AutomaticDiscountApplication" ||
            item.discountApplication.__typename === "ManualDiscountApplication",
        )
        .reduce((prev, curr) => prev + parseFloat(curr.allocatedAmount.amount), 0) || 0
    );
  }, [order]);

  // 상시 할인 이름
  const autoDiscountTitle = useMemo(() => {
    if (!order) return "";

    const titles = new Set<string>();

    order.lineItems.edges
      .flatMap(({ node }) => node.discountAllocations)
      .forEach((item) => {
        if (
          item.discountApplication.__typename === "AutomaticDiscountApplication" ||
          item.discountApplication.__typename === "ManualDiscountApplication"
        ) {
          titles.add(item.discountApplication.title);
        }
      });

    if (titles.has("Employee")) {
      return "Employee";
    }

    if (titles.has("Friends")) {
      return "Friends";
    }

    return Array.from(titles).join(", ");
  }, [order]);

  // 할인 코드 금액
  const codeDiscountAmount = useMemo(() => {
    if (!order) return 0;

    return (
      order.lineItems.edges
        .flatMap(({ node }) => node.discountAllocations)
        .filter((item) => item.discountApplication.__typename === "DiscountCodeApplication")
        .reduce((prev, curr) => prev + parseFloat(curr.allocatedAmount.amount), 0) || 0
    );
  }, [order]);

  // 할인 코드 이름
  const codeDiscountTitle = useMemo(() => {
    if (!order) return "";

    const titles = new Set<string>();

    order.discountApplications.edges.forEach(({ node }) => {
      if (node.__typename === "DiscountCodeApplication") {
        titles.add(node.code);
      }
    });

    order.lineItems.edges
      .flatMap(({ node }) => node.discountAllocations)
      .forEach((item) => {
        if (item.discountApplication.__typename === "DiscountCodeApplication") {
          titles.add(item.discountApplication.code);
        }
      });

    return Array.from(titles).join(", ");
  }, [order]);

  // 부가세 금액
  const taxAmount = useMemo(() => {
    if (order) {
      return parseFloat(order.totalTax?.amount || "0");
    }

    return parseFloat(orderInfo?.totalTax.amount || "0");
  }, [order, orderInfo]);

  // 관세 금액
  const dutyAmount = useMemo(() => {
    if (order) {
      return parseFloat(order.totalPrice.amount) - subTotalAmount - taxAmount - shippingAmount;
    }

    return parseFloat(orderInfo?.totalDuties.amount || "0");
  }, [order, orderInfo, subTotalAmount, taxAmount, shippingAmount]);

  // 주문 총 금액
  const totalAmount = useMemo(
    () => subTotalAmount + taxAmount + dutyAmount + shippingAmount,
    [dutyAmount, shippingAmount, subTotalAmount, taxAmount],
  );

  const shippingTitle = useMemo(() => {
    if (order?.shippingAddress?.countryCodeV2 === "KR") {
      return "CJ 대한통운";
    }

    return order?.customAttributes.find((item) => item.key === "shippingTitle")?.value || "Shipping";
  }, [order]);

  return {
    totalQuantity,
    shippingAmount,
    taxAmount,
    dutyAmount,
    totalAmount,
    subTotalAmount,
    lineItemAmount,
    refundAmount,
    autoDiscountAmount,
    autoDiscountTitle,
    codeDiscountAmount,
    codeDiscountTitle,
    shippingTitle,
  };
}
